import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiSearch, 
  FiDownload, 
  FiMail, 
  FiCalendar, 
  FiUser, 
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiEye
} from 'react-icons/fi';
import {
  fetchApplicationsByJob,
  fetchJobPostById,
  updateApplicationStatus as updateStatusService,
} from '../../../services/jobApplicationsService';
import './Applications.scss';

const Applications = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [jobPost, setJobPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const fetchJobPost = useCallback(async () => {
    try {
      const jobData = await fetchJobPostById(jobId);
      if (jobData) setJobPost(jobData);
    } catch (error) {
      console.error('Error fetching job post:', error);
    }
  }, [jobId]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const apps = await fetchApplicationsByJob(jobId);
      if (sortBy === 'firstName') {
        apps.sort((a, b) => {
          const left = String(a.firstName || '').toLowerCase();
          const right = String(b.firstName || '').toLowerCase();
          return sortOrder === 'asc' ? left.localeCompare(right) : right.localeCompare(left);
        });
      } else if (sortBy === 'createdAt') {
        apps.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt || 0).getTime();
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt || 0).getTime();
          return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
        });
      }
      setApplications(apps);
    } catch (error) {
      console.error('Error fetching applications:', error);
      // If applications collection doesn't exist, set empty array
      setApplications([]);
    } finally {
      setLoading(false);
    }
  }, [jobId, sortBy, sortOrder]);

  useEffect(() => {
    fetchJobPost();
    fetchApplications();
  }, [fetchJobPost, fetchApplications]);

  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await updateStatusService({
        jobId,
        applicationId,
        status: newStatus,
      });
      fetchApplications();
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Error updating application status');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'accepted':
      case 'hired':
        return 'success';
      case 'rejected':
        return 'error';
      case 'shortlisted':
        return 'info';
      case 'interview':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
      case 'hired':
        return <FiCheckCircle />;
      case 'rejected':
        return <FiXCircle />;
      case 'shortlisted':
      case 'interview':
        return <FiClock />;
      default:
        return <FiFileText />;
    }
  };

  // Filter and search logic
  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      (app.firstName && app.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (app.lastName && app.lastName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (app.email && app.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (app.phone && app.phone.includes(searchTerm)) ||
      (app.resumeUrl && app.resumeUrl.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentApplications = filteredApplications.slice(startIndex, endIndex);

  if (loading && !jobPost) {
    return (
      <div className="applications-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="applications-management">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/admin/job-posts')}>
          <FiArrowLeft /> Back to Job Posts
        </button>
        <div className="header-content">
          <h1>
            {jobPost ? `Applications for ${jobPost.title}` : 'Applications'}
          </h1>
          <p>{jobPost ? `${jobPost.company} • ${jobPost.department || 'N/A'}` : 'View and manage job applications'}</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <FiFileText />
          </div>
          <div className="stat-content">
            <h3>{applications.length}</h3>
            <p>Total Applications</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">
            <FiCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{applications.filter(app => app.status === 'shortlisted' || app.status === 'accepted' || app.status === 'hired').length}</h3>
            <p>Shortlisted</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <FiClock />
          </div>
          <div className="stat-content">
            <h3>{applications.filter(app => app.status === 'pending' || app.status === 'new').length}</h3>
            <p>Pending Review</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon info">
            <FiCalendar />
          </div>
          <div className="stat-content">
            <h3>{applications.filter(app => app.status === 'interview').length}</h3>
            <p>Interview Scheduled</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-controls">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="pending">Pending</option>
            <option value="shortlisted">Shortlisted</option>
            <option value="interview">Interview</option>
            <option value="accepted">Accepted</option>
            <option value="hired">Hired</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="filter-select"
          >
            <option value="createdAt-desc">Newest First</option>
            <option value="createdAt-asc">Oldest First</option>
            <option value="firstName-asc">Name A-Z</option>
            <option value="firstName-desc">Name Z-A</option>
          </select>
        </div>
      </div>

      {/* Applications Table */}
      <div className="applications-table-container">
        <table className="applications-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Contact</th>
              <th>Status</th>
              <th>Applied Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentApplications.map((application) => (
              <tr key={application.id}>
                <td>
                  <div className="candidate-cell">
                    <div className="candidate-avatar">
                      {application.firstName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="candidate-info">
                      <h4>{`${application.firstName || ''} ${application.lastName || ''}`.trim() || 'N/A'}</h4>
                      {application.currentPosition && (
                        <p>{application.currentPosition}</p>
                      )}
                    </div>
                  </div>
                </td>
                <td>
                  <div className="contact-cell">
                    {application.email && (
                      <div className="contact-item">
                        <FiMail />
                        <a href={`mailto:${application.email}`}>{application.email}</a>
                      </div>
                    )}
                    {application.phone && (
                      <div className="contact-item">
                        <FiUser />
                        <span>{application.phone}</span>
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <span className={`status-badge ${getStatusColor(application.status || 'new')}`}>
                    {getStatusIcon(application.status || 'new')}
                    {(application.status || 'new').charAt(0).toUpperCase() + (application.status || 'new').slice(1)}
                  </span>
                </td>
                <td>{formatDate(application.createdAt || application.appliedAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon view"
                      onClick={() => setSelectedApplication(application)}
                      title="View Details"
                      type="button"
                    >
                      <FiEye />
                    </button>
                    {application.resumeUrl && (
                      <a
                        href={application.resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-icon download"
                        title="Download Resume"
                      >
                        <FiDownload />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {currentApplications.length === 0 && (
          <div className="no-applications">
            <p>No applications found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="modal-overlay" onClick={() => setSelectedApplication(null)}>
          <div className="modal-content application-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Application Details</h2>
              <button className="close-btn" onClick={() => setSelectedApplication(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="application-details">
                <div className="detail-section">
                  <h3>Personal Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Full Name</label>
                      <p>{`${selectedApplication.firstName || ''} ${selectedApplication.lastName || ''}`.trim() || 'N/A'}</p>
                    </div>
                    <div className="detail-item">
                      <label>Email</label>
                      <p><a href={`mailto:${selectedApplication.email}`}>{selectedApplication.email || 'N/A'}</a></p>
                    </div>
                    <div className="detail-item">
                      <label>Phone</label>
                      <p>{selectedApplication.phone || 'N/A'}</p>
                    </div>
                    {selectedApplication.location && (
                      <div className="detail-item">
                        <label>Location</label>
                        <p>{selectedApplication.location}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedApplication.currentPosition && (
                  <div className="detail-section">
                    <h3>Professional Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Current Position</label>
                        <p>{selectedApplication.currentPosition}</p>
                      </div>
                      {selectedApplication.currentCompany && (
                        <div className="detail-item">
                          <label>Current Company</label>
                          <p>{selectedApplication.currentCompany}</p>
                        </div>
                      )}
                      {selectedApplication.yearsOfExperience && (
                        <div className="detail-item">
                          <label>Years of Experience</label>
                          <p>{selectedApplication.yearsOfExperience}</p>
                        </div>
                      )}
                      {selectedApplication.expectedSalary && (
                        <div className="detail-item">
                          <label>Expected Salary</label>
                          <p>{selectedApplication.expectedSalary}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedApplication.coverLetter && (
                  <div className="detail-section">
                    <h3>Cover Letter</h3>
                    <div className="detail-content">
                      <p>{selectedApplication.coverLetter}</p>
                    </div>
                  </div>
                )}

                {selectedApplication.additionalInfo && (
                  <div className="detail-section">
                    <h3>Additional Information</h3>
                    <div className="detail-content">
                      <p>{selectedApplication.additionalInfo}</p>
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <h3>Application Status</h3>
                  <div className="status-actions">
                    <span className={`status-badge large ${getStatusColor(selectedApplication.status || 'new')}`}>
                      {getStatusIcon(selectedApplication.status || 'new')}
                      {(selectedApplication.status || 'new').charAt(0).toUpperCase() + (selectedApplication.status || 'new').slice(1)}
                    </span>
                    <div className="status-buttons">
                      <button
                        className="btn-status shortlisted"
                        onClick={() => updateApplicationStatus(selectedApplication.id, 'shortlisted')}
                        disabled={selectedApplication.status === 'shortlisted'}
                      >
                        Shortlist
                      </button>
                      <button
                        className="btn-status interview"
                        onClick={() => updateApplicationStatus(selectedApplication.id, 'interview')}
                        disabled={selectedApplication.status === 'interview'}
                      >
                        Schedule Interview
                      </button>
                      <button
                        className="btn-status accepted"
                        onClick={() => updateApplicationStatus(selectedApplication.id, 'accepted')}
                        disabled={selectedApplication.status === 'accepted'}
                      >
                        Accept
                      </button>
                      <button
                        className="btn-status rejected"
                        onClick={() => updateApplicationStatus(selectedApplication.id, 'rejected')}
                        disabled={selectedApplication.status === 'rejected'}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                </div>

                {selectedApplication.resumeUrl && (
                  <div className="detail-section">
                    <h3>Resume</h3>
                    <a
                      href={selectedApplication.resumeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-download"
                    >
                      <FiDownload /> Download Resume
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button onClick={() => setSelectedApplication(null)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;

